/**
 * Responsibility: decide where natural rock arches stand.
 *
 * ## One authoritative placement rule, consulted two ways
 *
 * `archAt(slot)` is the only thing in the world that knows whether a slot holds an arch and what shape it is.
 * Placement walks it over a tile's slots; the clearance test walks it over the slots near a query point so rock
 * columns can keep out of the span. Both go through the same function, so the footprint columns are told to avoid
 * is by construction the footprint an arch will actually occupy. `city.js` does the same thing for the same
 * reason — deriving a footprint twice is how trees ended up inside towers.
 *
 * ## Why the clearance test can ignore the ground
 *
 * Placement rejects an arch on steep or uneven ground, and picks its heading from the terrain; the clearance test
 * does neither, so it reserves a circle around a few arches that were never built. That is deliberate on both
 * counts. Over-reserving costs a rock column here and there in a spot that would have been fine, which nobody can
 * see, and leaves a clearing around a landmark. Under-reserving puts a spire through a span, which everybody can
 * see. It also keeps the test free of terrain sampling, so it stays cheap enough to call once per column candidate
 * and answerable at any coordinate — which is what a column standing near a tile edge needs.
 *
 * Contains no Three.js and no DOM access, so it is fully unit testable.
 */

import { createLocalRng, deriveSeed } from './rng.js';
import { archFeet, archWidestRadius } from './archGeometry.js';
import { ARCHES } from '../config.js';

/** Linear interpolation, for turning a 0..1 roll into a range. */
function lerp(from, to, t) {
  return from + (to - from) * t;
}

/**
 * Range of slot indices whose centres fall inside a span.
 *
 * One tile owns each slot, so an arch near a tile edge is emitted exactly once. Shared shape with the column
 * grid; kept local rather than exported because the two grids are independent and coupling them would imply a
 * relationship that does not exist.
 */
function slotRange(start, length, spacing) {
  return {
    first: Math.ceil(start / spacing - 0.5),
    last: Math.ceil((start + length) / spacing - 0.5) - 1,
  };
}

/** Slot index a coordinate falls in, the inverse of the slot centre formula. */
function slotAt(coordinate, spacing) {
  return Math.round(coordinate / spacing - 0.5);
}

/**
 * Largest an arch can ever be, so the clearance test knows how far to look and jitter knows its slack.
 *
 * Derived from the config rather than written down again, because a raised `MAX_SPAN` that did not reach these
 * would silently break both.
 */
const MAX_HALF_SPAN = ARCHES.MAX_SPAN / 2;
const MAX_LEG_RADIUS = ARCHES.MAX_SPAN * ARCHES.CROWN_THICKNESS_RATIO * ARCHES.LEG_THICKENING;
const MAX_REACH = MAX_HALF_SPAN + MAX_LEG_RADIUS + ARCHES.CLEARANCE;

/**
 * Build the shared slot rule.
 *
 * @param {number} seed - Base world seed.
 * @param {string} biomeId - Derives an independent stream, so mesa and canyon do not place identical arches
 *   where they happen to meet.
 * @returns {(slotX: number, slotZ: number, sampleWeight: Function) => object|null}
 */
function createArchSlots(seed, biomeId) {
  const archSeed = deriveSeed(seed, `arches:${biomeId}`);

  /** Jitter room left after reserving the largest possible arch on each side, plus a gap. */
  const slack = Math.max(0, (ARCHES.SPACING - 2 * MAX_REACH - ARCHES.CLEARANCE) / 2);

  return function archAt(slotX, slotZ, sampleWeight) {
    const centerX = (slotX + 0.5) * ARCHES.SPACING;
    const centerZ = (slotZ + 0.5) * ARCHES.SPACING;

    const weight = sampleWeight(centerX, centerZ);
    if (weight <= 0) return null;

    /*
      One stream per slot, keyed by slot index rather than by tile, so an arch does not depend on which tile is
      being built or the order tiles are visited in.

      Roll order is part of the world's identity. Reordering these, or inserting one before an existing one,
      moves and reshapes every arch at every seed. Append only.
    */
    const random = createLocalRng(archSeed, slotX, slotZ);

    const presenceRoll = random();
    const spanRoll = random();
    const heightRoll = random();
    const headingRoll = random();
    const spinRoll = random();
    const jitterRollX = random();
    const jitterRollZ = random();

    // Density scales with biome weight, so arches thin out towards a border rather than stopping at a line.
    if (presenceRoll >= ARCHES.DENSITY * weight) return null;

    const span = lerp(ARCHES.MIN_SPAN, ARCHES.MAX_SPAN, spanRoll);
    const halfSpan = span / 2;
    const height = span * lerp(ARCHES.MIN_HEIGHT_RATIO, ARCHES.MAX_HEIGHT_RATIO, heightRoll);
    const crownThickness = span * ARCHES.CROWN_THICKNESS_RATIO;

    const arch = {
      kind: 'arch',
      x: centerX + (jitterRollX * 2 - 1) * slack,
      z: centerZ + (jitterRollZ * 2 - 1) * slack,
      halfSpan,
      height,
      crownThickness,

      /*
        Where the search for a workable orientation starts. The final heading is picked from the terrain, but the
        candidates are offset from a rolled value so two arches on similar ground do not come out parallel.
      */
      baseHeading: headingRoll * Math.PI * 2,

      sides: ARCHES.SIDES,
      spin: spinRoll,
      stops: ARCHES.STOPS,
      weight,
    };

    arch.legRadius = archWidestRadius(arch);

    /*
      The ground an arch claims: a circle of its own reach, regardless of which way it ends up facing.

      Generous on purpose. The heading is chosen from the terrain, so it is not known until the ground has been
      sampled — and a footprint that depended on it could not be evaluated at an arbitrary coordinate, which is
      exactly what a rock column standing near a tile edge needs to ask about. A circle is heading-free, so
      placement and clearance still share one authoritative number.
    */
    arch.footprintRadius = halfSpan + arch.legRadius + ARCHES.CLEARANCE;

    return arch;
  };
}

/**
 * Choose the orientation that fits the ground, and the height to plant it at.
 *
 * **This is what makes arches possible in mesa country at all.** Its terrain is quantised, so the ground is either
 * level or a cliff, and a 250-unit span dropped at a random angle straddles a terrace edge most of the time —
 * measured, only 22% of candidates survived. Trying a handful of orientations and keeping the flattest turns an
 * unlucky roll into a choice, and lands arches along terraces and canyon rims where a rock fin would actually be.
 *
 * @param {object} arch - From `archAt`.
 * @param {(x: number, z: number) => {elevation: number, slope: number}} sampleGroundDetail
 * @returns {{heading: number, feet: Array, groundY: number}|null} Null when no orientation works.
 */
function fitToGround(arch, sampleGroundDetail) {
  const centre = sampleGroundDetail(arch.x, arch.z);

  let best = null;

  for (let attempt = 0; attempt < ARCHES.HEADING_ATTEMPTS; attempt += 1) {
    // Half a turn covers every distinct orientation: an arch is symmetric about its own centre.
    const heading = arch.baseHeading + (attempt / ARCHES.HEADING_ATTEMPTS) * Math.PI;
    const feet = archFeet({ ...arch, heading });
    const probes = feet.map(([x, z]) => sampleGroundDetail(x, z));

    const elevations = [centre.elevation, ...probes.map((probe) => probe.elevation)];
    const spread = Math.max(...elevations) - Math.min(...elevations);

    if (best && spread >= best.spread) continue;

    best = {
      heading,
      feet,
      spread,
      steepest: Math.max(centre.slope, ...probes.map((probe) => probe.slope)),
      lowest: Math.min(...elevations),
    };
  }

  if (!best) return null;
  if (best.spread > ARCHES.MAX_FOOT_SPREAD) return null;
  if (best.steepest > ARCHES.MAX_GROUND_SLOPE) return null;

  return {
    heading: best.heading,
    feet: best.feet,

    /*
      Sunk to the lowest ground it saw, so the arch cuts into a rise rather than leaving a leg in mid-air over a
      dip. Rock buried in rock is invisible; rock floating above it is not.
    */
    groundY: best.lowest - ARCHES.FOOT_SINK,
  };
}

/**
 * Build the arch placement function for a biome.
 *
 * @param {number} seed - Base world seed.
 * @param {string} biomeId
 * @returns {(region: object, context: object) => Array<object>}
 */
export function createRockArches(seed, biomeId) {
  const archAt = createArchSlots(seed, biomeId);

  /**
   * @param {{originX: number, originZ: number, size: number}} region - Tile bounds.
   * @param {object} context
   * @param {(x: number, z: number) => number} context.sampleWeight - This biome's weight.
   * @param {(x: number, z: number) => {elevation: number, slope: number}} context.sampleGroundDetail
   * @param {(x: number, z: number, radius: number) => boolean} [context.isReserved]
   * @returns {Array<object>} Arch descriptions.
   */
  return function collectStructures(region, { sampleWeight, sampleGroundDetail, isReserved }) {
    const xRange = slotRange(region.originX, region.size, ARCHES.SPACING);
    const zRange = slotRange(region.originZ, region.size, ARCHES.SPACING);

    const arches = [];

    for (let slotX = xRange.first; slotX <= xRange.last; slotX += 1) {
      for (let slotZ = zRange.first; slotZ <= zRange.last; slotZ += 1) {
        const arch = archAt(slotX, slotZ, sampleWeight);
        if (!arch) continue;

        /*
          Ground at the centre and at both feet, for each orientation tried. Sampling only the centre says nothing
          about whether the far leg has anything under it, and an arch is hundreds of units across.
        */
        const fitted = fitToGround(arch, sampleGroundDetail);
        if (!fitted) continue;

        Object.assign(arch, fitted);

        // Keep clear of another biome's structures, tested at both feet since they are far apart.
        const blocked = [[arch.x, arch.z], ...arch.feet].some(([x, z]) =>
          isReserved?.(x, z, arch.legRadius + ARCHES.CLEARANCE),
        );
        if (blocked) continue;

        arches.push(arch);
      }
    }

    return arches;
  };
}

/**
 * Build the test that says whether a point is inside an arch's ground footprint.
 *
 * A circle of the arch's own reach, because the heading is not knowable here — it is chosen from the terrain, and
 * this test has to answer at an arbitrary coordinate without sampling any. Over-reserving costs a rock column
 * slot or two beside each arch, which nobody can see, and leaves a small clearing around a landmark. Under-
 * reserving would put a spire through the span, which is the one thing an arch cannot survive.
 *
 * @param {number} seed - Base world seed.
 * @param {string} biomeId
 * @returns {(x: number, z: number, radius: number, sampleWeight: Function) => boolean}
 */
export function createArchClearance(seed, biomeId) {
  const archAt = createArchSlots(seed, biomeId);

  return function isInsideAnArch(x, z, radius, sampleWeight) {
    const slotX = slotAt(x, ARCHES.SPACING);
    const slotZ = slotAt(z, ARCHES.SPACING);

    /*
      The neighbouring ring of slots as well as this one. An arch's footprint reaches `MAX_REACH` from its centre,
      which is well under the slot spacing but far more than zero, so a query just inside one slot can still land
      on the arch belonging to the next.
    */
    for (let dx = -1; dx <= 1; dx += 1) {
      for (let dz = -1; dz <= 1; dz += 1) {
        const arch = archAt(slotX + dx, slotZ + dz, sampleWeight);
        if (!arch) continue;

        if (Math.hypot(x - arch.x, z - arch.z) < arch.footprintRadius + radius) return true;
      }
    }

    return false;
  };
}
